/**
 * Stair LAYOUT solver (STAIR round, 2026-08-21 — Matt's five-issue audit; U-STAIR
 * round same day): ONE authoritative computation of a stair's plan-space layout from
 * its drawn centerline + calculator params. The 3D generator, plan linework,
 * pick/hover body, draw-time ghost, run-length normalization, and the tread-number
 * annotation ALL consume this, so what highlights, what builds, and what draws can
 * never disagree (the old generator improvised each locally — the "ghost stair
 * length" bug was the pick body reading the raw drawn path while the boxes stopped
 * at the riser count).
 *
 * Conventions (all carried from the Phase-3 generator, now centralized):
 * - The path is the run CENTERLINE in walking direction; width extends w/2 each side.
 * - Runs stop w/2 short of every interior node (the landing owns the corner). This
 *   holds against LANDING SEGMENTS too: the connector strip is centered on the drawn
 *   line, so a riser landing exactly on the strip's edge (w/2 from the node) is what
 *   keeps the landing slab clear of the adjacent run's treads.
 * - LANDING SEGMENTS (U-STAIR round): a path segment flagged `edge.landing` carries
 *   NO treads — its span merges with the adjacent runs' cross-lines into ONE landing
 *   slab. This is what makes a U drawable (up-run, across, back-run) without the
 *   solver inserting a stray step on the connector. A RUN segment that fits zero
 *   treads is treated the same way automatically (auto-merge), so a tight U heals
 *   even unflagged.
 * - Treads that outrun the drawn path continue along the last run segment's
 *   direction — `complete` reports false and `usedPts` carries the TRUE centerline.
 * - All z's are RELATIVE to the stair base (caller adds zBase = level + baseOffset).
 * - Riser faces exist at every tread's NEAR edge (the nose) PLUS the top riser at the
 *   final tread's far edge, which lands ON the upper floor — risers = treads + 1
 *   lines, bottom (s=0) and top included.
 *
 * RUN-BASED STAIRS (2026-10-02, Matt: "I need to be able to draw a run with landings …
 * For a u-shaped stair, for example, I can draw the first run, then at a different
 * location draw the second run, and the landing is automatically drawn between the two
 * … when I change the width of one run in a multi-run stair, it should only impact that
 * one run"). Revit's component-stair Run tool: each run is drawn on its own (start → end)
 * and the landing between consecutive runs is DERIVED. Stored in the same path: run
 * segments with a landing connector segment between them (the U-STAIR model), opted into
 * with the stair param `runLayout: 'runs'` — in that mode
 *   - a run beside a landing connector starts / stops EXACTLY at its drawn node (no w/2
 *     setback — the drawn run end IS its riser line);
 *   - the landing is a rectangle in the INCOMING run's frame spanning both runs' cross-
 *     lines, as deep as the geometry needs — a U (the runs side by side) defaults to the
 *     wider run's width, an L fills the corner square, a straight run fills its gap —
 *     and a per-landing `edge.landingDepthFt` may deepen it, never shallower than what
 *     connects the runs;
 *   - every run may carry its own width (`edge.widthFt` on the run segment's start node,
 *     the roof edge-attr convention), the stair width as the default.
 * Stairs without `runLayout` lay out exactly as before (per-run widths still apply).
 */
import type { Path2D, Pt2 } from './types'
import { stairRun, type StairRun } from './stair'
import { stairCurveLayout, type StairCurve } from './stairCurve'
import { stairRecipeOf, stairRecipeLayout } from './stairRecipe'

export interface StairLayoutOpts {
  widthFt: number
  floorToFloorFt: number
  maxRiserFt: number
  treadDepthFt: number
}

export interface StairLayoutLeg {
  /** source segment index in the input pts (normalization maps legs back to nodes) */
  seg: number
  /** the leg's path node (segment start) + unit direction / left normal */
  a: Pt2
  u: Pt2
  n: Pt2
  /** stations along the leg axis from `a`: first tread near edge → last tread far edge */
  s0: number
  s1: number
  /** tread index range on this leg: [kStart, kEnd) */
  kStart: number
  kEnd: number
  /** walking z (relative) at the leg's start — the floor/landing it climbs from */
  z0: number
  /** drawn segment length (s1 may exceed it on the last leg — `complete` false) */
  segLen: number
  /** this run's own width (RUN-BASED STAIRS — per-run widths) */
  w: number
  /** STAIR BUILDER: the walking z (relative) this run's last riser arrives at — a landing or the
   *  top floor — and whether that is the top. Set for configured stairs (a split stair's two arms
   *  both arrive at the top, so "the next run" can't say); read through stairLegEnd. */
  zEnd?: number
  top?: boolean
}

/** where a run arrives: its own zEnd when the layout set one, else the next run's start (or the
 *  top floor for the last) — the one place every consumer asks */
export function stairLegEnd(layout: { legs: StairLayoutLeg[] }, i: number, riseFt: number): { zEnd: number; top: boolean } {
  const g = layout.legs[i]
  if (g && typeof g.zEnd === 'number') return { zEnd: g.zEnd, top: !!g.top }
  const last = i >= layout.legs.length - 1
  return { zEnd: last ? riseFt : layout.legs[i + 1].z0, top: last }
}

export interface StairLayout {
  calc: StairRun
  legs: StairLayoutLeg[]
  /** one per tread, plan quad [nearL, farL, farR, nearR]-ish winding, zTop relative */
  treads: { i: number; quad: Pt2[]; zTop: number }[]
  /** landing walking polygons — ONE per gap between consecutive runs (healed hulls;
   *  landing segments and zero-tread connectors merge into the same slab) */
  landings: { poly: Pt2[]; zTop: number
    /** RUN-BASED STAIRS: the connector segment (its start node holds landingDepthFt),
     *  the incoming run's frame, the far edge, the current + minimum depth */
    seg?: number; u?: Pt2; n?: Pt2; endAt?: Pt2; depth?: number; minDepth?: number; c0?: number; c1?: number
    /** STAIR BUILDER: the recipe turn this landing is (absent = added automatically), and which of
     *  its edges moves when its depth changes with the top held still ('near' / 'far') */
    turn?: number; grip?: 'near' | 'far' }[]
  /**
   * Riser FACE lines across the width — index 0 is the bottom riser at the run start,
   * the last is the top riser landing on the upper floor. `z` = face bottom (what the
   * plan cut classifies against: any face rising from at/below the cut reads solid),
   * `zTop` = face top (the nosing edge, what the 3D edge lines draw at).
   */
  riserLines: { pts: [Pt2, Pt2]; z: number; zTop: number }[]
  /** climbing run side lines per leg (split at the cut plane downstream) */
  sideLines: { pts: [Pt2, Pt2]; z0: number; z1: number }[]
  /**
   * Full plan footprint ring — legs + landing hulls walked into one boundary
   * (U-STAIR round; used by selection emphasis, plan pick, and the draw ghost).
   * Null when the boundary walk can't chain — callers fall back to a corridor.
   */
  outline: Pt2[] | null
  /** SKETCHED STAIRS: the two sides of `outline`, each bottom → top (left first) —
   *  what Convert to Sketch turns into the sketch's edges */
  edgeChains: [Pt2[], Pt2[]] | null
  /** the centerline actually consumed — drawn nodes (landing connectors included),
   *  ghost tail trimmed, last point at the true run end */
  usedPts: Pt2[]
  /** true when the drawn path had room for every tread without extension */
  complete: boolean
  /** STAIR STUDIO stage 3: a spiral / helical stair's geometry (no straight legs) */
  curve?: StairCurve
  /** STAIR BUILDER (bim-plan 33): winder treads — each its own polygon, one riser up from the
   *  last (zTop relative); `k` = its position among all the stair's steps */
  winders?: { poly: Pt2[]; zTop: number; k: number }[]
  /** each winder turn: its outline, the point its tread lines fan from, the walk-line points
   *  its m + 1 tread lines pass through (front of the first winder → back of the last), the
   *  position of its first winder, and what the code measures (walk-line depth, narrowest end) */
  winderZones?: { poly: Pt2[]; q: Pt2; walk: Pt2[]; k0: number; m: number; walkDepthFt: number; narrowFt: number }[]
  /** STAIR BUILDER: a branching stair's third edge (the landing's far side and the arms' outer
   *  sides) — not one of the two guard sides */
  backChain?: Pt2[]
  /** STAIR BUILDER: a swept (curved / sculpted) stair's centreline + what the code measures */
  sweep?: import('./stairSweep').StairSweepInfo
}

/** per-SEGMENT landing flags off a stair path (the roof convention: attrs live on
 *  the node the edge LEAVES) — the channel every stairLayout caller reads */
export const stairLandingFlags = (path: Path2D): boolean[] =>
  path.nodes.map((nd) => !!nd.edge?.landing)

/** RUN-BASED STAIRS: per-segment run widths (`edge.widthFt`, undefined = the stair's) */
export const stairSegWidths = (path: Path2D): (number | undefined)[] =>
  path.nodes.map((nd) => {
    const v = (nd.edge as { widthFt?: unknown } | undefined)?.widthFt
    return typeof v === 'number' && v > 0 ? v : undefined
  })

/** RUN-BASED STAIRS: per-landing-segment depth overrides (`edge.landingDepthFt`) */
export const stairLandingDepths = (path: Path2D): (number | undefined)[] =>
  path.nodes.map((nd) => {
    const v = (nd.edge as { landingDepthFt?: unknown } | undefined)?.landingDepthFt
    return typeof v === 'number' && v > 0 ? v : undefined
  })

export interface StairLayoutExtra {
  widths?: (number | undefined)[]
  landingDepths?: (number | undefined)[]
  /** RUN-BASED STAIRS: runs beside landings keep their drawn ends; derived landings */
  runs?: boolean
  /** draw-time preview: the last run stops at its drawn end and an unfinished stair
   *  still lays out (risers so far) instead of returning null */
  partial?: boolean
}

/**
 * The ONE call every consumer makes for an element's stair layout: reads the path's
 * landing flags, per-run widths, landing depths, and the stair's `runLayout` mode.
 */
export function stairLayoutForPath(path: Path2D, opts: StairLayoutOpts, params?: Record<string, unknown>): StairLayout | null {
  if (!path?.nodes || path.nodes.length < 2) return null
  // STAIR BUILDER (bim-plan 33): a configured stair lays out from its recipe; its path is only
  // where its top nosing is and which way its top run climbs
  // STAIR STUDIO stage 3: a spiral or helical stair lays out on its own curve
  if (params?.stairShape === 'spiral' || params?.stairShape === 'helical') return stairCurveLayout(path, opts, params)
  const recipe = stairRecipeOf(params)
  if (recipe) {
    const a = path.nodes[0].pt, b = path.nodes[1].pt
    return stairRecipeLayout(recipe, [a[0], a[1]], [b[0] - a[0], b[1] - a[1]], opts)
  }
  return stairLayout(
    path.nodes.map((nd) => [nd.pt[0], nd.pt[1]] as Pt2),
    opts,
    stairLandingFlags(path),
    { widths: stairSegWidths(path), landingDepths: stairLandingDepths(path), runs: params?.runLayout === 'runs' },
  )
}

/** flat cross-line through `at` along ±n·w/2 */
function crossLine(at: Pt2, n: Pt2, w: number): [Pt2, Pt2] {
  return [
    [at[0] - n[0] * (w / 2), at[1] - n[1] * (w / 2)],
    [at[0] + n[0] * (w / 2), at[1] + n[1] * (w / 2)],
  ]
}

const ptsClose = (a: Pt2, b: Pt2): boolean => Math.hypot(a[0] - b[0], a[1] - b[1]) < 1e-6

/** convex hull (monotone chain), CCW; dedupes within eps */
function convexHull(pts: Pt2[]): Pt2[] {
  const uniq: Pt2[] = []
  for (const p of pts) {
    if (!uniq.some((q) => ptsClose(q, p))) uniq.push(p)
  }
  if (uniq.length <= 3) return uniq
  const s = [...uniq].sort((a, b) => a[0] - b[0] || a[1] - b[1])
  const cross = (o: Pt2, a: Pt2, b: Pt2) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])
  const lower: Pt2[] = []
  for (const p of s) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop()
    lower.push(p)
  }
  const upper: Pt2[] = []
  for (let i = s.length - 1; i >= 0; i--) {
    const p = s[i]
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop()
    upper.push(p)
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)]
}

/** intersection of two lines given as point + direction; null when near-parallel */
function lineLine(p: Pt2, d: Pt2, q: Pt2, e: Pt2): Pt2 | null {
  const den = d[0] * e[1] - d[1] * e[0]
  if (Math.abs(den) < 1e-9) return null
  const t = ((q[0] - p[0]) * e[1] - (q[1] - p[1]) * e[0]) / den
  return [p[0] + d[0] * t, p[1] + d[1] * t]
}

/** a landing-connector piece (a flagged landing segment, or a zero-tread run) */
interface LandingConn { a: Pt2; b: Pt2; n: Pt2; seg: number }

/**
 * LANDING HEALING (STAIR round item 4; U-STAIR round merged regions + miter limit):
 * ONE polygon per gap between consecutive runs — the convex region from the incoming
 * run's TRUE last riser cross-line (a fractional-tread remainder never leaves a gap),
 * through any landing-connector segments' swept width, to the outgoing run's first
 * riser cross-line. With no connectors (the corner-node L case) the legs' own
 * side-line miters close the shape — perpendicular legs reproduce the classic w×w
 * square exactly; skewed legs get the correct trapezoid/pentagon; a miter reaching
 * further than 1.25·w past either cross-line endpoint BEVELS at that cap (the
 * linework miter-limit idea) so a near-straight kink can never balloon the landing.
 */
function healedLanding(
  inLeg: { a: Pt2; u: Pt2; n: Pt2; s1: number; w: number },
  conns: LandingConn[],
  outA: Pt2, outU: Pt2, outN: Pt2, outS0: number,
  wOut: number,
  runBased = false,
  depthOverride?: number,
  meta?: { depth?: number; minDepth?: number; endAt?: Pt2; c0?: number; c1?: number },
): Pt2[] {
  const wIn = inLeg.w
  const h = Math.max(wIn, wOut) / 2
  const hIn = wIn / 2, hOut = wOut / 2
  const endAt: Pt2 = [inLeg.a[0] + inLeg.u[0] * inLeg.s1, inLeg.a[1] + inLeg.u[1] * inLeg.s1]
  const startAt: Pt2 = [outA[0] + outU[0] * outS0, outA[1] + outU[1] * outS0]
  const E = crossLine(endAt, inLeg.n, wIn)
  const S = crossLine(startAt, outN, wOut)
  const pts: Pt2[] = [E[0], E[1], S[0], S[1]]
  if (runBased && conns.length) {
    // RUN-BASED STAIRS: the derived landing — a rectangle in the incoming run's frame
    // over both runs' cross-lines, from the incoming run's last riser forward as deep
    // as the outgoing run's start needs (an L's corner square, a straight run's gap);
    // runs side by side (a U, nothing ahead to reach) default to the wider run's width.
    // A depth override may deepen it — never shallower than what connects the runs.
    const u = inLeg.u, n = inLeg.n
    const al = (p: Pt2) => (p[0] - endAt[0]) * u[0] + (p[1] - endAt[1]) * u[1]
    const ac = (p: Pt2) => (p[0] - endAt[0]) * n[0] + (p[1] - endAt[1]) * n[1]
    const sAl = [al(S[0]), al(S[1])]
    const geomDepth = Math.max(0, ...sAl)
    const back = Math.min(0, ...sAl)
    const opposed = u[0] * outU[0] + u[1] * outU[1] < -0.5
    // MID-LANDING DEPTH (2026-10-04, Matt: "I just drew two runs and it messed up the
    // mid-landing"): runs side by side (a U) turn around ON the landing, so it is never
    // shallower than the stair is wide (the code minimum: a landing as deep as the
    // stair's width). It used to take whatever gap the runs' ends happened to leave —
    // two runs drawn 4" short of each other's end gave a 4" landing. Deeper drawn or
    // dialled depths still win; the depth grip can't go below this.
    const floor = opposed ? Math.max(wIn, wOut, geomDepth) : geomDepth
    const auto = opposed ? floor : (geomDepth > 1e-6 ? geomDepth : 0)
    const depth = Math.min(Math.max(depthOverride && depthOverride > 0 ? depthOverride : auto, floor), floor + 30)
    if (depth - back < 1e-6) return []
    const cs = [ac(E[0]), ac(E[1]), ac(S[0]), ac(S[1])]
    const c0 = Math.min(...cs), c1 = Math.max(...cs)
    if (meta) { meta.depth = depth; meta.minDepth = floor > 1e-6 ? floor : 0.5; meta.endAt = endAt; meta.c0 = c0; meta.c1 = c1 }
    const P = (a: number, c: number): Pt2 => [endAt[0] + u[0] * a + n[0] * c, endAt[1] + u[1] * a + n[1] * c]
    return [P(back, c0), P(depth, c0), P(depth, c1), P(back, c1)]
  }
  if (conns.length) {
    // CONNECTOR LANDINGS ARE RECTANGLES (U-STAIR round r2 — Matt: "landings are
    // getting strange angle cuts"): a raw convex hull bridges mismatched riser
    // stations (run 1's fractional last riser vs run 2's w/2 start) with diagonal
    // bevel edges. Instead: the ORIENTED BOUNDING RECT in the connector's own frame
    // over the riser cross-lines + the swept strip — the classic Revit U-landing.
    // Where the rect reaches past a run's riser it tucks flat UNDER that run's body
    // (the landing top IS the run's base plane), so overcoverage is benign.
    for (const c of conns) {
      pts.push(
        [c.a[0] - c.n[0] * h, c.a[1] - c.n[1] * h], [c.a[0] + c.n[0] * h, c.a[1] + c.n[1] * h],
        [c.b[0] - c.n[0] * h, c.b[1] - c.n[1] * h], [c.b[0] + c.n[0] * h, c.b[1] + c.n[1] * h],
      )
    }
    const dx = conns[0].b[0] - conns[0].a[0], dy = conns[0].b[1] - conns[0].a[1]
    const len = Math.hypot(dx, dy)
    if (len > 1e-9) {
      const u: Pt2 = [dx / len, dy / len]
      const n: Pt2 = [-u[1], u[0]]
      let u0 = Infinity, u1 = -Infinity, n0 = Infinity, n1 = -Infinity
      for (const p of pts) {
        const pu = p[0] * u[0] + p[1] * u[1]
        const pn = p[0] * n[0] + p[1] * n[1]
        if (pu < u0) u0 = pu; if (pu > u1) u1 = pu
        if (pn < n0) n0 = pn; if (pn > n1) n1 = pn
      }
      return [
        [u[0] * u0 + n[0] * n0, u[1] * u0 + n[1] * n0],
        [u[0] * u1 + n[0] * n0, u[1] * u1 + n[1] * n0],
        [u[0] * u1 + n[0] * n1, u[1] * u1 + n[1] * n1],
        [u[0] * u0 + n[0] * n1, u[1] * u0 + n[1] * n1],
      ]
    }
  } else {
    // corner-node landing: close with the legs' own side-line miters (bevel-capped)
    const cap = Math.max(wIn, wOut) * 1.25
    for (const sgn of [1, -1] as const) {
      const pIn: Pt2 = [inLeg.a[0] + inLeg.n[0] * sgn * hIn, inLeg.a[1] + inLeg.n[1] * sgn * hIn]
      const pOut: Pt2 = [outA[0] + outN[0] * sgn * hOut, outA[1] + outN[1] * sgn * hOut]
      const o = lineLine(pIn, inLeg.u, pOut, outU)
      if (!o) continue
      // a sane miter sits strictly between the two cross-lines (an acute
      // switchback's fly-off intersection must not fold the landing over treads)
      const beyondIn = inLeg.u[0] * (o[0] - endAt[0]) + inLeg.u[1] * (o[1] - endAt[1])
      const beforeOut = outU[0] * (o[0] - startAt[0]) + outU[1] * (o[1] - startAt[1])
      if (beyondIn < -1e-6 || beforeOut > 1e-6) continue
      const pInEnd: Pt2 = [endAt[0] + inLeg.n[0] * sgn * hIn, endAt[1] + inLeg.n[1] * sgn * hIn]
      const pOutEnd: Pt2 = [startAt[0] + outN[0] * sgn * hOut, startAt[1] + outN[1] * sgn * hOut]
      const t1 = Math.hypot(o[0] - pInEnd[0], o[1] - pInEnd[1])
      const t2 = Math.hypot(o[0] - pOutEnd[0], o[1] - pOutEnd[1])
      if (t1 <= cap && t2 <= cap) {
        pts.push(o)
      } else {
        // miter limit: bevel — clamp along each side line instead of the far tip
        pts.push([pInEnd[0] + inLeg.u[0] * Math.min(t1, cap), pInEnd[1] + inLeg.u[1] * Math.min(t1, cap)])
        pts.push([pOutEnd[0] - outU[0] * Math.min(t2, cap), pOutEnd[1] - outU[1] * Math.min(t2, cap)])
      }
    }
  }
  return convexHull(pts)
}

/** distance from a point to a segment */
function segDist(p: Pt2, a: Pt2, b: Pt2): number {
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const len2 = dx * dx + dy * dy
  const t = len2 < 1e-12 ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2))
  return Math.hypot(p[0] - (a[0] + dx * t), p[1] - (a[1] + dy * t))
}

/** strictly inside a polygon (on-boundary counts as OUTSIDE — boundary anchors are fine) */
function strictlyInPoly(p: Pt2, poly: Pt2[]): boolean {
  for (let i = 0; i < poly.length; i++) {
    if (segDist(p, poly[i], poly[(i + 1) % poly.length]) < 1e-5) return false
  }
  let inside = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i], b = poly[j]
    if ((a[1] > p[1]) !== (b[1] > p[1])
      && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) inside = !inside
  }
  return inside
}

/** smallest positive ray parameter where p + t·dir crosses the polygon boundary */
function rayExitPoly(p: Pt2, dir: Pt2, poly: Pt2[]): number | null {
  let best: number | null = null
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length]
    const ex = b[0] - a[0], ey = b[1] - a[1]
    const den = dir[0] * ey - dir[1] * ex
    if (Math.abs(den) < 1e-12) continue
    const t = ((a[0] - p[0]) * ey - (a[1] - p[1]) * ex) / den
    const s = ((a[0] - p[0]) * dir[1] - (a[1] - p[1]) * dir[0]) / den
    if (t > 1e-9 && s >= -1e-9 && s <= 1 + 1e-9 && (best === null || t < best)) best = t
  }
  return best
}

/**
 * Boundary arc of a convex (CCW) landing hull from anchor P to anchor Q, choosing the
 * side whose boundary path never runs through the `avoid` anchors (the opposite
 * side's cross-line endpoints) — the piece the footprint-ring walk threads between
 * two runs. Anchors may sit mid-edge (the U-case: collinear cross endpoints get
 * popped by the hull) — they're inserted onto their edge first. The avoidance test is
 * segment-distance over the WHOLE candidate polyline (P → arc → Q), not vertex
 * identity — a direct hull edge that passes straight over the other side's anchors
 * (the U's bottom edge) is rejected even though it contains no vertices at all.
 * Null when an anchor isn't on the hull (callers fall back to a corridor body).
 */
function hullArc(hull: Pt2[], P: Pt2, Q: Pt2, avoid: Pt2[]): Pt2[] | null {
  // insert an anchor onto its hull edge when it isn't already a vertex
  const withAnchor = (h: Pt2[], pt: Pt2): Pt2[] | null => {
    if (h.some((hp) => ptsClose(hp, pt))) return h
    for (let i = 0; i < h.length; i++) {
      if (segDist(pt, h[i], h[(i + 1) % h.length]) < 1e-5) {
        const out = [...h]
        out.splice(i + 1, 0, pt)
        return out
      }
    }
    return null
  }
  let h = withAnchor(hull, P)
  if (h) h = withAnchor(h, Q)
  if (!h) return null
  const idxOf = (pt: Pt2) => h!.findIndex((hp) => ptsClose(hp, pt))
  const i = idxOf(P), j = idxOf(Q)
  if (i < 0 || j < 0) return null
  if (i === j) return []
  const n = h.length
  const walk = (dir: 1 | -1): Pt2[] => {
    const out: Pt2[] = []
    for (let t = (i + dir + n) % n; t !== j; t = (t + dir + n) % n) out.push(h![t])
    return out
  }
  const runsOverAvoid = (arc: Pt2[]): boolean => {
    const poly = [P, ...arc, Q]
    for (let s = 0; s < poly.length - 1; s++) {
      for (const av of avoid) {
        if (ptsClose(av, P) || ptsClose(av, Q)) continue
        if (segDist(av, poly[s], poly[s + 1]) < 1e-4) return true
      }
    }
    return false
  }
  const fw = walk(1)
  if (!runsOverAvoid(fw)) return fw
  const bw = walk(-1)
  if (!runsOverAvoid(bw)) return bw
  return null
}

export function stairLayout(pts: Pt2[], opts: StairLayoutOpts, landingFlags?: (boolean | undefined)[], extra: StairLayoutExtra = {}): StairLayout | null {
  if (!pts || pts.length < 2) return null
  const w = opts.widthFt || 3.5
  const td = opts.treadDepthFt || 11 / 12
  const calc = stairRun(opts.floorToFloorFt || 10, opts.maxRiserFt || 7.5 / 12, td)
  if (calc.treads < 1) return null
  const r = calc.riserFt

  const legs: StairLayoutLeg[] = []
  const treads: StairLayout['treads'] = []
  const landings: StairLayout['landings'] = []
  const riserLines: StairLayout['riserLines'] = []
  const sideLines: StairLayout['sideLines'] = []

  // the last RUN segment (flagged landing segments never extend treads past a
  // drawn end — a trailing landing segment can't be "the last leg")
  let lastRunSeg = -1
  for (let seg = 0; seg < pts.length - 1; seg++) {
    const len = Math.hypot(pts[seg + 1][0] - pts[seg][0], pts[seg + 1][1] - pts[seg][1])
    if (len >= 1e-9 && !landingFlags?.[seg]) lastRunSeg = seg
  }
  if (lastRunSeg < 0) return null

  let k = 0
  let pendingConns: LandingConn[] = []
  for (let seg = 0; seg < pts.length - 1 && k < calc.treads; seg++) {
    const a = pts[seg], b = pts[seg + 1]
    const dx = b[0] - a[0], dy = b[1] - a[1]
    const segLen = Math.hypot(dx, dy)
    if (segLen < 1e-9) continue
    const u: Pt2 = [dx / segLen, dy / segLen]
    const n: Pt2 = [-u[1], u[0]]

    // a flagged landing segment carries no treads — its span joins the landing gap
    if (landingFlags?.[seg]) {
      if (legs.length) pendingConns.push({ a, b, n, seg })
      continue
    }
    // this run's own width (per-run widths), the stair's by default
    const ws = extra.widths?.[seg] || w

    const isLast = seg === lastRunSeg
    // runs ALWAYS start/stop w/2 short of an interior node — including against a
    // landing segment: the connector strip is centered on the drawn line, so the
    // adjacent run's first/last riser landing exactly on the strip's edge is what
    // keeps the landing slab from overlapping the run's own treads
    // RUN-BASED STAIRS: beside a landing connector the run keeps its drawn end exactly
    const prevLanding = !!extra.runs && pendingConns.length > 0
    const nextLanding = !!extra.runs && !!landingFlags?.[seg + 1]
    const segStartS = legs.length === 0 || prevLanding ? 0 : ws / 2
    const segCapS = isLast ? (extra.partial ? segLen : Infinity) : nextLanding ? segLen : segLen - ws / 2
    const kStart = k
    const z0 = kStart * r
    let s = segStartS
    while (k < calc.treads && s + td <= segCapS + 1e-9) {
      const s1 = s + td
      const p0: Pt2 = [a[0] + u[0] * s, a[1] + u[1] * s]
      const p1: Pt2 = [a[0] + u[0] * s1, a[1] + u[1] * s1]
      treads.push({
        i: k,
        quad: [
          [p0[0] - n[0] * (ws / 2), p0[1] - n[1] * (ws / 2)],
          [p1[0] - n[0] * (ws / 2), p1[1] - n[1] * (ws / 2)],
          [p1[0] + n[0] * (ws / 2), p1[1] + n[1] * (ws / 2)],
          [p0[0] + n[0] * (ws / 2), p0[1] + n[1] * (ws / 2)],
        ],
        zTop: (k + 1) * r,
      })
      // the riser face this tread's nose sits on — bottom riser (k=0, s=0) included
      riserLines.push({ pts: crossLine(p0, n, ws), z: k * r, zTop: (k + 1) * r })
      s = s1
      k++
    }
    if (k > kStart) {
      // ONE merged landing per gap between runs — built only now that the outgoing
      // run is real, from the incoming run's true last riser through any connector
      // segments to this run's first riser (auto-merge: a zero-tread run joined the
      // connectors below instead of minting its own landing)
      if (legs.length > 0) {
        const meta: { depth?: number; minDepth?: number; endAt?: Pt2; c0?: number; c1?: number } = {}
        const inL = legs[legs.length - 1]
        const poly = healedLanding(inL, pendingConns, a, u, n, segStartS, ws, !!extra.runs,
          pendingConns.length ? extra.landingDepths?.[pendingConns[0].seg] : undefined, meta)
        if (poly.length >= 3) {
          landings.push(meta.depth != null && pendingConns.length
            ? { poly, zTop: kStart * r, seg: pendingConns[0].seg, u: inL.u, n: inL.n, endAt: meta.endAt, depth: meta.depth, minDepth: meta.minDepth, c0: meta.c0, c1: meta.c1 }
            : { poly, zTop: kStart * r })
        }
      }
      pendingConns = []
      const zSide0 = kStart * r
      const zSide1 = k * r
      for (const sgn of [1, -1] as const) {
        sideLines.push({
          pts: [
            [a[0] + u[0] * segStartS + n[0] * sgn * (ws / 2), a[1] + u[1] * segStartS + n[1] * sgn * (ws / 2)],
            [a[0] + u[0] * s + n[0] * sgn * (ws / 2), a[1] + u[1] * s + n[1] * sgn * (ws / 2)],
          ],
          z0: zSide0, z1: zSide1,
        })
      }
      legs.push({ seg, a, u, n, s0: segStartS, s1: s, kStart, kEnd: k, z0, segLen, w: ws })
    } else if (legs.length) {
      // U-STAIR auto-merge: a run segment that fits zero treads IS a landing
      // connector — never a second overlapping node-landing
      pendingConns.push({ a, b, n, seg })
    }
  }
  if (!legs.length || (k < calc.treads && !extra.partial)) return null   // degenerate path (all segments tiny)

  // TOP RISER — the face that lands ON the upper floor (risers = treads + 1). No
  // tread beyond it: the upper floor slab is the walking surface.
  const last = legs[legs.length - 1]
  const topAt: Pt2 = [last.a[0] + last.u[0] * last.s1, last.a[1] + last.u[1] * last.s1]
  riserLines.push({
    pts: crossLine(topAt, last.n, last.w),
    z: calc.treads * r,
    zTop: calc.risers * r,
  })

  // the true centerline: every drawn node through the finishing run (landing
  // connectors included — the walking line crosses the landing), then the run end
  const usedPts: Pt2[] = [...pts.slice(0, last.seg + 1).map((p) => [p[0], p[1]] as Pt2), topAt]
  const complete = last.s1 <= last.segLen + 1e-9

  // FOOTPRINT RING (U-STAIR round): chain leg rectangles and landing hulls into one
  // boundary — left sides forward, the end cap, right sides back. Landing hull arcs
  // thread between consecutive runs on the side being walked. Any failure to chain
  // falls back to null (callers use a corridor over usedPts).
  const cornerOf = (leg: StairLayoutLeg, s: number, sgn: 1 | -1): Pt2 => [
    leg.a[0] + leg.u[0] * s + leg.n[0] * sgn * (leg.w / 2),
    leg.a[1] + leg.u[1] * s + leg.n[1] * sgn * (leg.w / 2),
  ]
  let outline: Pt2[] | null = null
  let edgeChains: [Pt2[], Pt2[]] | null = null
  {
    // U-STAIR round r2: a rectangular landing can SWALLOW a run's corner (run 1's
    // fractional last riser sits short of run 2's start station — the rect spans
    // both, so run 2's inner start corner lands strictly inside it). The boundary
    // anchor is then the point where that run's side line EXITS the landing —
    // marched along the leg's own direction, away from the joint.
    const anchorOf = (leg: StairLayoutLeg, s: number, sgn: 1 | -1, poly: Pt2[], away: 1 | -1): Pt2 => {
      const c = cornerOf(leg, s, sgn)
      if (!strictlyInPoly(c, poly)) return c
      const dir: Pt2 = [leg.u[0] * away, leg.u[1] * away]
      const t = rayExitPoly(c, dir, poly)
      return t != null ? [c[0] + dir[0] * t, c[1] + dir[1] * t] : c
    }
    const left: Pt2[] = [cornerOf(legs[0], legs[0].s0, 1)]
    const rightFwd: Pt2[] = [cornerOf(legs[0], legs[0].s0, -1)]   // reversed at the end
    let ok = true
    for (let i = 1; i < legs.length && ok; i++) {
      const hull = landings[i - 1]?.poly
      if (!hull) { ok = false; break }
      const aL0 = anchorOf(legs[i - 1], legs[i - 1].s1, 1, hull, -1)
      const aL1 = anchorOf(legs[i], legs[i].s0, 1, hull, 1)
      const aR0 = anchorOf(legs[i - 1], legs[i - 1].s1, -1, hull, -1)
      const aR1 = anchorOf(legs[i], legs[i].s0, -1, hull, 1)
      const arcL = hullArc(hull, aL0, aL1, [aR0, aR1])
      const arcR = hullArc(hull, aR0, aR1, [aL0, aL1])
      if (!arcL || !arcR) { ok = false; break }
      left.push(aL0, ...arcL, aL1)
      rightFwd.push(aR0, ...arcR, aR1)
    }
    if (ok) {
      left.push(cornerOf(legs[legs.length - 1], legs[legs.length - 1].s1, 1))
      rightFwd.push(cornerOf(legs[legs.length - 1], legs[legs.length - 1].s1, -1))
      const dd = (pl: Pt2[]) => pl.filter((p, i) => i === 0 || !ptsClose(p, pl[i - 1]))
      edgeChains = [dd(left), dd([...rightFwd])]
      const ring = [...left, ...rightFwd.reverse()]
      // dedupe consecutive coincident points (shared corners on perpendicular Ls)
      outline = ring.filter((p, i) => !ptsClose(p, ring[(i + 1) % ring.length]))
      if (outline.length < 3) outline = null
    }
  }

  return { calc, legs, treads, landings, riserLines, sideLines, outline, edgeChains, usedPts, complete }
}

/**
 * Riser faces that fit within the DRAWN path (the draw-time "N of M risers" readout):
 * every riser on a non-final leg is within by construction; the last leg counts faces
 * whose station sits inside its drawn segment length. The top riser (at the final
 * tread's far edge) counts when the full run fits.
 */
export function stairRisersWithinDrawn(layout: StairLayout): number {
  const last = layout.legs[layout.legs.length - 1]
  let n = 0
  for (const leg of layout.legs) {
    if (leg !== last) { n += leg.kEnd - leg.kStart; continue }
    const td = (leg.s1 - leg.s0) / Math.max(1, leg.kEnd - leg.kStart)
    for (let k = leg.kStart; k < leg.kEnd; k++) {
      const sNear = leg.s0 + (k - leg.kStart) * td
      if (sNear <= leg.segLen + 1e-9) n++
    }
  }
  if (layout.complete) n++   // the top riser face at the run end
  return Math.min(n, layout.calc.risers)
}

/**
 * RUN-LENGTH NORMALIZATION (STAIR round, audit item 2 — "the run line can be longer
 * than the riser count… a ghost stair length"): rewrite a stair's drawn path so its
 * arc length IS the true run — the end node moves to the final riser's station
 * (clamping a too-long path, extending a too-short one), and trailing ghost nodes
 * beyond the finishing leg drop. LANDING SEGMENTS (U-STAIR round) keep their drawn
 * length verbatim — they're landing-geometry intent, not run consumption. Node ids
 * (and per-edge attrs) are preserved so grips and undo stay coherent. Returns null
 * when the path is already normalized (or can't be laid out).
 */
export function normalizeStairRunPath(path: Path2D, opts: StairLayoutOpts, params?: Record<string, unknown>): Path2D | null {
  if (!path || path.closed || !Array.isArray(path.nodes) || path.nodes.length < 2) return null
  if (stairRecipeOf(params)) return null    // a configured stair's path is only its placement
  const layout = stairLayoutForPath(path, opts, params)
  if (!layout || layout.curve || !layout.legs.length) return null
  const last = layout.legs[layout.legs.length - 1]
  const endPt = layout.usedPts[layout.usedPts.length - 1]
  const keep = last.seg + 1              // nodes 0..last.seg survive as-is
  const endNode = path.nodes[last.seg + 1]
  const cur = endNode.pt
  if (
    path.nodes.length === keep + 1
    && Math.hypot(cur[0] - endPt[0], cur[1] - endPt[1]) < 1e-6
  ) return null
  return {
    closed: false,
    nodes: [
      ...path.nodes.slice(0, keep).map((nd) => ({ ...nd })),
      { ...endNode, pt: [endPt[0], endPt[1]] as Pt2 },
    ],
  }
}
